(() => {
  if (customElements.get('bc-site-header')) return;
  class BlackCometHeader extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (el, type, fn, opts = {}) => el.addEventListener(type, fn, { ...opts, signal: this.controller.signal });
      this.mobile = matchMedia('(max-width: 1000px)');
      this.dialog = this.querySelector('dialog');
      this.accordions = [...this.querySelectorAll('[data-bc-accordion]')];
      this.toggle = this.querySelector('[data-bc-open]');
      this.expansion = this.querySelector('.bc-nav__expansion');
      this.backdrop = this.querySelector('.bc-nav__backdrop');
      this.triggers = [...this.querySelectorAll('[data-bc-menu]')];
      this.panelObserver = new ResizeObserver(() => {
        if (this.mobile.matches || !this.activeId || !this.classList.contains('is-expanded')) return;
        const panel = this.querySelector(`[data-bc-panel="${this.activeId}"]`);
        const height = panel?.getBoundingClientRect().height;
        if (height && Math.abs(height - this.desktopTargetHeight) > .5) {
          this.animateDesktopHeight(this.expansion.getBoundingClientRect().height, height);
        }
      });
      this.querySelectorAll('[data-bc-panel]').forEach(panel => this.panelObserver.observe(panel));
      this.lastScroll = window.scrollY;
      this.directionOrigin = this.lastScroll;
      this.lastDirection = 0;
      this.headerMode = this.lastScroll <= 2 ? 'top' : 'compact';
      this.activeId = null;
      this.triggers.forEach(trigger => {
        on(trigger, 'click', event => {
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const id = trigger.dataset.bcMenu;
          if (this.activeId === id && this.clickArmed === trigger) return;
          event.preventDefault();
          this.openDesktop(id);
          this.clickArmed = trigger;
        });
        on(trigger, 'pointerenter', event => {
          if (this.mobile.matches || this.expandTimer || event.pointerType !== 'mouse') return;
          clearTimeout(this.closeTimer);
          this.openTimer = setTimeout(() => this.openDesktop(trigger.dataset.bcMenu), 100);
        });
        on(trigger, 'pointerleave', () => clearTimeout(this.openTimer));
        on(trigger, 'keydown', async event => {
          if (!['ArrowDown', ' '].includes(event.key)) return;
          event.preventDefault();
          const opened = await this.openDesktop(trigger.dataset.bcMenu);
          if (opened && this.activeId === trigger.dataset.bcMenu) this.querySelector(`[data-bc-panel="${trigger.dataset.bcMenu}"] a`)?.focus({ preventScroll: true });
        });
      });
      on(this, 'pointerdown', () => this.classList.add('is-pointer-input'), { passive: true });
      on(document, 'keydown', () => { this.classList.remove('is-pointer-input'); this.syncBrand(); });
      const shell = this.querySelector('.bc-nav__shell');
      on(shell, 'pointerenter', () => clearTimeout(this.closeTimer));
      on(shell, 'pointerleave', event => {
        if (this.mobile.matches || event.pointerType !== 'mouse' || this.expandTimer) return;
        clearTimeout(this.openTimer);
        this.closeTimer = setTimeout(() => this.closeDesktop(), 240);
      });
      on(this.backdrop, 'click', () => this.closeDesktop(true));
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.closeDesktop(); });
      on(this, 'focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => { if (!this.contains(document.activeElement)) this.closeDesktop(); this.syncBrand(); }, 0);
      });
      on(document, 'keydown', event => {
        if (event.key === 'Escape' && this.activeId) { event.preventDefault(); this.closeDesktop(true); }
      });
      on(this.toggle, 'click', () => this.openMobile());
      on(this.querySelector('[data-bc-close]'), 'click', () => this.closeMobile());
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.closeMobile(); });
      on(this.dialog, 'click', event => { if (event.target === this.dialog && event.clientY > this.dialog.getBoundingClientRect().bottom) this.closeMobile(); });
      this.accordions.forEach(button => on(button, 'click', () => this.toggleAccordion(button)));
      on(this, 'click', event => {
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[href]');
        if (!link) return;
        if (link.getAttribute('href') === '#') { event.preventDefault(); return; }
        const url = new URL(link.href, location.href);
        const target = url.hash && url.origin === location.origin && url.pathname === location.pathname ? document.getElementById(decodeURIComponent(url.hash.slice(1))) : null;
        this.closeDesktop();
        if (this.dialog.open) this.closeMobile(false, true);
        if (target) {
          event.preventDefault();
          history.pushState(null, '', url.hash);
          target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        }
      });
      on(window, 'scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.updateScroll(); });
      }, { passive: true });
      on(this.mobile, 'change', () => { this.brandHovered = false; this.closeDesktop(); if (this.dialog.open) this.closeMobile(false, true); this.updateScroll(); });
      on(document, 'shopify:block:select', event => {
        const id = event.detail?.blockId;
        const trigger = this.triggers.find(el => el.dataset.bcMenu === id) || this.triggers.find(el => this.querySelector(`[data-bc-panel="${el.dataset.bcMenu}"] [data-shopify-editor-block*="${CSS.escape(id || '')}"]`));
        if (trigger && !this.mobile.matches) this.openDesktop(trigger.dataset.bcMenu);
      });
      this.setupBrand(on);
      this.updateScroll();
    }
    setupBrand(on) {
      this.brandWords = [...this.querySelectorAll('[data-bc-brand-word]')];
      if (!this.brandWords.length) { this.revealNavigation(false); return; }
      this.brandReduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.symbolImages = [...this.querySelectorAll('[data-bc-symbol-animation]')];
      this.symbolIntroPending = this.dataset.entryIntro === 'pending' && window.scrollY <= 2;
      const firstEntry = this.symbolIntroPending;
      this.brandMode = null;
      this.brandReady = false;
      this.brandPhrase = 'Black Comet';
      this.setBrandText(firstEntry ? '' : 'Black Comet');
      this.classList.toggle('is-brand-ready', !firstEntry);
      on(this.brandReduced, 'change', () => { this.stopBrand(); this.brandMode = null; this.syncBrand(); });
      on(document, 'visibilitychange', () => this.syncBrand());
      on(this.querySelector('.bc-nav__shell'), 'pointerenter', event => {
        if (this.mobile.matches || event.pointerType !== 'mouse') return;
        this.brandHovered = true;
        this.syncBrand();
      });
      on(this.querySelector('.bc-nav__shell'), 'pointerleave', event => {
        if (this.mobile.matches || event.pointerType !== 'mouse') return;
        this.brandHovered = false;
        this.syncBrand();
      });
      on(this, 'focusin', () => this.syncBrand());
      const ready = () => {
        if (!this.isConnected || this.controller?.signal.aborted) return;
        this.brandReady = true;
        this.classList.add('is-brand-ready');
        this.syncBrand();
      };
      if (!firstEntry) {
        this.dataset.symbolIntro = 'complete';
        this.revealNavigation(false);
        ready();
        return;
      }
      const fontReady = document.fonts?.load ? document.fonts.load('400 16px "Black Comet Orbitron"').catch(() => {}) : Promise.resolve();
      const source = this.symbolImages[0]?.dataset.bcSymbolAnimation;
      const animationReady = source ? fetch(source, { signal: this.controller.signal }).then(response => {
        if (!response.ok) throw new Error('Logo animation unavailable');
        return response.blob();
      }).then(blob => { this.symbolIntroBlob = blob; return true; }).catch(() => false) : Promise.resolve(false);
      Promise.all([fontReady, animationReady]).then(([, loaded]) => {
        this.symbolIntroAvailable = loaded && typeof URL.createObjectURL === 'function';
        ready();
      });
    }
    hideNavigation() {
      clearTimeout(this.navigationTimer);
      this.navigationTimer = null;
      this.dataset.navIntro = 'pending';
    }
    revealNavigation(animate = true) {
      if (this.dataset.navIntro === 'ready' || this.dataset.navIntro === 'revealing') return;
      this.dataset.navIntro = animate && !this.brandReduced?.matches ? 'revealing' : 'ready';
      if (this.dataset.navIntro === 'revealing') this.navigationTimer = setTimeout(() => {
        this.navigationTimer = null;
        this.dataset.navIntro = 'ready';
      }, 750);
      document.dispatchEvent(new CustomEvent('bc:brand-ready'));
    }
    releaseSymbolImage() {
      this.symbolImages?.forEach(image => image.removeAttribute('src'));
      if (this.symbolIntroUrl) URL.revokeObjectURL(this.symbolIntroUrl);
      this.symbolIntroUrl = null;
    }
    cancelSymbolIntro() {
      this.symbolIntroRun = (this.symbolIntroRun || 0) + 1;
      ['symbolStartTimer', 'symbolTextTimer', 'symbolEndTimer'].forEach(key => { clearTimeout(this[key]); this[key] = null; });
      this.symbolIntroCleanup?.();
      this.symbolIntroCleanup = null;
      this.symbolIntroPlaying = this.symbolIntroHoldingText = false;
      this.dataset.symbolIntro = 'complete';
      this.releaseSymbolImage();
    }
    playSymbolIntro() {
      const run = this.symbolIntroRun = (this.symbolIntroRun || 0) + 1;
      this.symbolIntroPlaying = this.symbolIntroHoldingText = true;
      const writeName = () => {
        if (run !== this.symbolIntroRun || !this.isConnected || this.controller?.signal.aborted) return;
        clearTimeout(this.symbolTextTimer);
        this.symbolTextTimer = null;
        this.symbolIntroHoldingText = false;
        this.brandMode = null;
        this.syncBrand();
      };
      const finish = () => {
        if (run !== this.symbolIntroRun) return;
        clearTimeout(this.symbolStartTimer);
        clearTimeout(this.symbolEndTimer);
        this.symbolStartTimer = this.symbolEndTimer = null;
        this.symbolIntroCleanup?.();
        this.symbolIntroCleanup = null;
        this.symbolIntroPlaying = false;
        this.dataset.symbolIntro = 'complete';
        this.releaseSymbolImage();
        if (this.symbolIntroHoldingText) writeName();
      };
      const image = this.symbolImages[0];
      const start = () => {
        if (run !== this.symbolIntroRun) return;
        clearTimeout(this.symbolStartTimer);
        this.symbolStartTimer = null;
        this.dataset.symbolIntro = 'playing';
        // The supplied GIF has a 340ms opening frame before its first visible stroke.
        this.symbolTextTimer = setTimeout(writeName, 1340);
        // Freeze on the vector before the animation's 5s loop can restart.
        this.symbolEndTimer = setTimeout(finish, 3600);
      };
      image.addEventListener('load', start, { once: true });
      image.addEventListener('error', finish, { once: true });
      this.symbolIntroCleanup = () => { image.removeEventListener('load', start); image.removeEventListener('error', finish); };
      this.symbolStartTimer = setTimeout(finish, 2000);
      this.releaseSymbolImage();
      this.symbolIntroUrl = URL.createObjectURL(this.symbolIntroBlob);
      this.symbolImages.forEach(item => { item.src = this.symbolIntroUrl; });
    }

    setBrandText(value) {
      this.brandText = value;
      this.dataset.brandTrademark = String(value === 'Black Comet');
      this.brandWords?.forEach(word => { word.textContent = value; });
    }
    stopBrand() {
      clearTimeout(this.brandTick);
      clearTimeout(this.brandSwap);
      this.brandTick = this.brandSwap = null;
      this.brandToken = (this.brandToken || 0) + 1;
      delete this.dataset.brandTyping;
    }
    animateBrandText(target, erase = false, done) {
      const token = this.brandToken;
      this.dataset.brandTyping = 'true';
      const step = () => {
        if (token !== this.brandToken || (!['top', 'interaction'].includes(this.brandMode) && !(erase && (this.brandMode === 'hidden' || this.symbolIntroHoldingText)))) return;
        const next = erase ? this.brandText.slice(0, -1) : target.slice(0, this.brandText.length + 1);
        this.setBrandText(next);
        if (erase ? next.length > 0 : next !== target) this.brandTick = setTimeout(step, erase ? 24 : 65);
        else {
          this.brandTick = null;
          delete this.dataset.brandTyping;
          if (!erase && target === 'Black Comet') this.revealNavigation();
          done?.();
        }
      };
      this.brandTick = setTimeout(step, erase ? 24 : 65);
    }
    syncBrand() {
      if (!this.brandReady || !this.isConnected || this.controller?.signal.aborted) return;
      const compact = this.classList.contains('is-compact');
      const interacting = this.brandHovered || this.activeId || this.dialog.open || (!this.classList.contains('is-pointer-input') && this.querySelector(':focus-visible'));
      const atTop = window.scrollY <= 2 && !compact;
      const mode = document.hidden ? 'hidden' : interacting ? 'interaction' : atTop ? 'top' : compact ? 'hidden' : 'travel';
      // Only the first home load can play the entry. Scroll, navigation and
      // interrupted entries never arm it again during this session.
      if (!atTop || document.hidden || this.brandReduced.matches) {
        this.symbolIntroPending = false;
        if (this.symbolIntroPlaying) this.cancelSymbolIntro();
        this.revealNavigation(false);
      }
      if (this.symbolIntroHoldingText) {
        this.brandMode = this.dataset.brandMode = mode;
        this.dataset.brandVisible = String(mode !== 'hidden');
        return;
      }
      const needsIntro = this.symbolIntroPending && atTop && ['top', 'interaction'].includes(mode);
      if (mode === this.brandMode && !needsIntro && !(document.hidden && this.brandTick)) return;
      const previous = this.brandMode;
      const wasVisible = this.dataset.brandVisible === 'true';
      this.stopBrand();
      this.brandMode = mode;
      this.dataset.brandMode = mode;
      if (mode === 'hidden') {
        this.revealNavigation(false);
        if (!this.symbolIntroPlaying) this.dataset.symbolIntro = 'complete';
        // Keep the visible wordmark in place until its last letter is erased.
        if (!document.hidden && !this.brandReduced.matches && wasVisible && this.brandText && previous === 'interaction') {
          this.dataset.brandVisible = 'true';
          this.animateBrandText('', true, () => { this.dataset.brandVisible = 'false'; });
        } else {
          this.dataset.brandVisible = 'false';
          this.setBrandText('');
        }
        return;
      }
      this.dataset.brandVisible = 'true';
      if (mode === 'travel') { if (!this.symbolIntroPlaying) this.dataset.symbolIntro = 'complete'; this.setBrandText(this.brandPhrase); this.revealNavigation(false); return; }
      this.brandPhrase = 'Black Comet';
      if (this.brandReduced.matches || !this.symbolIntroAvailable) {
        this.symbolIntroPending = false;
        this.dataset.symbolIntro = 'complete';
        if (this.brandReduced.matches) { this.setBrandText('Black Comet'); this.revealNavigation(false); return; }
      }
      if (this.symbolIntroPending) {
        this.hideNavigation();
        this.symbolIntroPending = false;
        this.symbolIntroPlaying = this.symbolIntroHoldingText = true;
        const draw = () => this.playSymbolIntro();
        if (wasVisible && this.brandText) this.animateBrandText('', true, draw);
        else { this.setBrandText(''); draw(); }
        return;
      }
      // A visible Black Comet already matches the interaction target.
      if (wasVisible && this.brandText === 'Black Comet') {
        this.revealNavigation();
        if (mode === 'top') this.scheduleBrandSwap();
        return;
      }
      const writeName = () => this.animateBrandText('Black Comet', false, () => {
        if (this.brandMode === 'top') this.scheduleBrandSwap();
      });
      if (wasVisible && 'Black Comet'.startsWith(this.brandText)) writeName();
      else if (wasVisible && this.brandText) this.animateBrandText('', true, writeName);
      else { this.setBrandText(''); writeName(); }
    }
    scheduleBrandSwap() {
      if (this.brandMode !== 'top' || this.brandReduced.matches) return;
      this.brandSwap = setTimeout(() => {
        this.brandSwap = null;
        this.brandPhrase = this.brandPhrase === 'Black Comet' ? 'Growth Partners' : 'Black Comet';
        this.animateBrandText('', true, () => {
          this.brandTick = setTimeout(() => {
            this.animateBrandText(this.brandPhrase, false, () => this.scheduleBrandSwap());
          }, 100);
        });
      }, 7000);
    }
    async openDesktop(id) {
      clearTimeout(this.closeTimer);
      if (this.mobile.matches) return false;
      if (this.activeId === id) return this.menuReady || true;
      const fromHeight = this.expansion.getBoundingClientRect().height;
      this.desktopPanelAnimation?.cancel();
      this.activeId = id;
      this.clickArmed = null;
      this.querySelectorAll('[data-bc-panel]').forEach(panel => { panel.hidden = panel.dataset.bcPanel !== id; });
      this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', String(trigger.dataset.bcMenu === id)));
      this.classList.remove('is-scroll-hidden', 'is-returning');
      if (this.expandTimer) return this.menuReady;
      if (!this.classList.contains('is-compact')) {
        // Finish the compact transition before revealing any submenu rows.
        this.classList.remove('is-top-flow');
        this.classList.add('is-compact', 'is-preparing-menu');
        this.syncBrand();
        this.menuReady = new Promise(resolve => {
          this.menuResolve = resolve;
          this.expandTimer = setTimeout(() => {
            this.expandTimer = null;
            this.classList.remove('is-preparing-menu');
            const reveal = !!this.activeId && !this.mobile.matches;
            if (reveal) this.revealDesktop();
            resolve(reveal);
            this.menuResolve = null;
            this.menuReady = null;
          }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 500);
        });
        return this.menuReady;
      }
      this.revealDesktop(fromHeight);
      return true;
    }
    animateDesktopHeight(from, to) {
      this.desktopHeightAnimation?.cancel();
      this.desktopHeightAnimation = null;
      this.desktopTargetHeight = to;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(from - to) < .5) return;
      const animation = this.expansion.animate([{ height: `${from}px` }, { height: `${to}px` }], {
        duration: 500,
        easing: 'cubic-bezier(.22,1,.36,1)'
      });
      this.desktopHeightAnimation = animation;
      animation.onfinish = () => {
        if (this.desktopHeightAnimation === animation) this.desktopHeightAnimation = null;
      };
    }
    revealDesktop(from = this.expansion.getBoundingClientRect().height) {
      this.expansion.inert = false;
      this.expansion.setAttribute('aria-hidden', 'false');
      this.backdrop.hidden = false;
      this.classList.add('is-expanded');
      const panel = this.querySelector(`[data-bc-panel="${this.activeId}"]`);
      this.animateDesktopHeight(from, panel.getBoundingClientRect().height);
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.desktopPanelAnimation = panel.animate([
          { opacity: 0, transform: 'translateY(6px)' },
          { opacity: 1, transform: 'translateY(0)' }
        ], { duration: 360, easing: 'cubic-bezier(.22,1,.36,1)' });
      }
      this.syncBrand();
    }
    closeDesktop(restoreFocus = false) {
      const fromHeight = this.expansion.getBoundingClientRect().height;
      this.desktopPanelAnimation?.cancel();
      clearTimeout(this.openTimer);
      clearTimeout(this.closeTimer);
      clearTimeout(this.expandTimer);
      this.expandTimer = null;
      this.menuResolve?.(false);
      this.menuResolve = null;
      this.menuReady = null;
      const trigger = this.triggers.find(el => el.dataset.bcMenu === this.activeId);
      this.activeId = null;
      this.clickArmed = null;
      this.triggers.forEach(el => el.setAttribute('aria-expanded', 'false'));
      this.expansion.inert = true;
      this.expansion.setAttribute('aria-hidden', 'true');
      this.backdrop.hidden = true;
      this.classList.remove('is-expanded', 'is-preparing-menu');
      this.animateDesktopHeight(fromHeight, 0);
      this.updateScroll();
      if (restoreFocus) trigger?.focus({ preventScroll: true });
    }
    updateMobileFooter(menuKey) {
      const footerKey = ['stories', 'resources'].includes(menuKey) ? menuKey : 'default';
      this.dialog.querySelectorAll('[data-bc-mobile-footer]').forEach(footer => {
        footer.hidden = footer.dataset.bcMobileFooter !== footerKey;
      });
    }
    resetMobile() {
      this.updateMobileFooter();
      this.accordions.forEach(button => {
        button.setAttribute('aria-expanded', 'false');
        button.closest('.bc-nav__mobile-item').classList.remove('is-open');
      });
      this.querySelectorAll('[data-bc-mobile-panel]').forEach(panel => {
        panel.inert = true;
        panel.setAttribute('aria-hidden', 'true');
      });
    }
    toggleAccordion(button) {
      if (this.dialog.classList.contains('is-closing')) return;
      this.mobileAnimation?.finish();
      const expand = button.getAttribute('aria-expanded') !== 'true';
      this.accordions.forEach(trigger => {
        const open = trigger === button && expand;
        trigger.setAttribute('aria-expanded', String(open));
        trigger.closest('.bc-nav__mobile-item').classList.toggle('is-open', open);
        const panel = this.querySelector(`[data-bc-mobile-panel="${trigger.dataset.bcAccordion}"]`);
        panel.inert = !open;
        panel.setAttribute('aria-hidden', String(!open));
      });
      this.updateMobileFooter(expand ? button.dataset.bcMenuKey : '');
      this.restoreMobileScroll();
    }
    restoreMobileScroll() {
      requestAnimationFrame(() => { if (this.dialog.open) window.scrollTo({ top: this.mobileScrollY, behavior: 'instant' }); });
    }
    animateMobileHeight(from, closing = false, onFinish) {
      this.mobileAnimation?.cancel();
      this.dialog.style.height = '';
      const to = closing ? this.querySelector('.bc-nav__rail').offsetHeight + 2 : this.dialog.getBoundingClientRect().height;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) { onFinish?.(); return; }
      this.dialog.classList.add('is-animating');
      this.mobileAnimation = this.dialog.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: closing ? 440 : 650, easing: 'cubic-bezier(.22,1,.36,1)' });
      this.mobileAnimation.onfinish = () => { this.dialog.classList.remove('is-animating'); this.mobileAnimation = null; onFinish?.(); };
    }
    openMobile() {
      if (this.dialog.open) return;
      this.resetMobile();
      this.closeDesktop();
      this.updateScroll();
      this.mobileScrollY = window.scrollY;
      const fromHeight = this.querySelector('.bc-nav__rail').offsetHeight + 2;
      this.classList.remove('is-scroll-hidden');
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('bc-nav-open');
      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      // Commit the starting width before moving the open menu into compact mode.
      this.dialog.getBoundingClientRect();
      this.classList.add('is-mobile-open');
      this.updateScroll();
      requestAnimationFrame(() => { if (this.dialog.open && !this.dialog.classList.contains('is-closing')) this.classList.add('is-menu-morphed'); });
      this.animateMobileHeight(fromHeight);
      this.querySelector('[data-bc-close]').focus({ preventScroll: true });
      this.restoreMobileScroll();
    }
    closeMobile(restoreFocus = true, immediate = false) {
      if (!this.dialog.open || this.dialog.classList.contains('is-closing')) return;
      const finish = () => {
        this.dialog.close();
        this.dialog.classList.remove('is-closing', 'is-animating');
        this.classList.remove('is-mobile-open', 'is-menu-morphed');
        this.resetMobile();
        this.toggle.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('bc-nav-open');
        if (restoreFocus) this.toggle.focus({ preventScroll: true });
        window.scrollTo({ top: this.mobileScrollY, behavior: 'instant' });
        this.updateScroll();
      };
      if (immediate) { this.mobileAnimation?.cancel(); finish(); return; }
      const from = this.dialog.getBoundingClientRect().height;
      this.dialog.classList.add('is-closing');
      this.classList.remove('is-menu-morphed');
      this.animateMobileHeight(from, true, finish);
    }
    updateScroll() {
      const y = Math.max(0, window.scrollY);
      if (this.dialog.open) {
        this.classList.add('is-compact');
        this.classList.remove('is-top-flow', 'is-scroll-hidden', 'is-returning');
        this.lastScroll = y;
        this.syncBrand();
        return;
      }
      if (this.activeId) {
        this.classList.add('is-compact');
        this.classList.remove('is-top-flow', 'is-scroll-hidden');
        this.lastScroll = y;
        this.syncBrand();
        return;
      }
      const direction = Math.sign(y - this.lastScroll);
      if (direction && direction !== this.lastDirection) this.directionOrigin = this.lastScroll;
      if (direction) this.lastDirection = direction;
      const railHeight = this.querySelector('.bc-nav__rail').offsetHeight;
      if (y <= 2) {
        this.headerMode = 'top';
        this.classList.remove('is-compact', 'is-scroll-hidden', 'is-returning');
      } else if (this.headerMode === 'top') {
        // The initial full-width header leaves with the document. It never
        // shrinks on the first downward scroll through the hero.
        if (direction < 0 && y > railHeight + 16 && this.directionOrigin - y > 12) {
          this.headerMode = 'compact';
          this.classList.add('is-compact', 'is-returning');
          clearTimeout(this.returnTimer);
          this.returnTimer = setTimeout(() => this.classList.remove('is-returning'), 600);
        }
      } else {
        this.classList.add('is-compact');
        const keyboardFocus = this.querySelector(':focus-visible');
        const hide = this.dataset.scrollHide === 'true' && y > 96 && direction > 0 && y - this.directionOrigin > 24;
        if (hide && !this.activeId && !keyboardFocus) { this.classList.remove('is-returning'); this.classList.add('is-scroll-hidden'); }
        if (direction < 0 || keyboardFocus || y <= 96) this.classList.remove('is-scroll-hidden');
      }
      this.classList.toggle('is-top-flow', this.headerMode === 'top');
      this.lastScroll = y;
      this.syncBrand();
    }
    disconnectedCallback() {
      this.panelObserver?.disconnect();
      this.desktopHeightAnimation?.cancel();
      this.desktopPanelAnimation?.cancel();
      this.cancelSymbolIntro();
      clearTimeout(this.navigationTimer);
      this.stopBrand();
      this.controller?.abort();
      this.menuResolve?.(false);
      cancelAnimationFrame(this.scrollFrame);
      ['openTimer','closeTimer','focusTimer','returnTimer','expandTimer'].forEach(key => clearTimeout(this[key]));
      if (this.dialog?.open) this.closeMobile(false, true);
      this.controller = null;
    }
  }
  customElements.define('bc-site-header', BlackCometHeader);
})();
